import React from 'react';
import { ArrowRight } from 'lucide-react';
interface HeroSectionProps {
  language: 'en' | 'es';
}
const LightStreaks = () => <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-60" data-id="element-106">
    <svg className="absolute w-[150%] h-[150%] -top-[25%] -left-[25%]" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-107">
      <path d="M-20,120 C20,80 60,40 120,-20" stroke="url(#gold-streak)" strokeWidth="1.5" filter="blur(6px)" fill="none" data-id="element-108" />
      <path d="M-10,130 C30,90 70,50 130,-10" stroke="url(#gold-streak)" strokeWidth="3" filter="blur(12px)" fill="none" data-id="element-109" />
      <path d="M0,140 C40,100 80,60 140,0" stroke="url(#gold-streak)" strokeWidth="0.5" filter="blur(2px)" fill="none" data-id="element-110" />
      <defs data-id="element-111">
        <linearGradient id="gold-streak" x1="0%" y1="100%" x2="100%" y2="0%" data-id="element-112">
          <stop offset="0%" stopColor="transparent" data-id="element-113" />
          <stop offset="30%" stopColor="#A8832A" data-id="element-114" />
          <stop offset="50%" stopColor="#E8D48B" data-id="element-115" />
          <stop offset="70%" stopColor="#C9A84C" data-id="element-116" />
          <stop offset="100%" stopColor="transparent" data-id="element-117" />
        </linearGradient>
      </defs>
    </svg>
  </div>;
export function HeroSection({
  language
}: HeroSectionProps) {
  return <section className="relative min-h-screen lg:min-h-[85vh] flex items-center overflow-hidden bg-luxury-bg" data-id="element-118">
      {/* Background Gradients & Effects */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-luxury-emerald rounded-full blur-[150px] opacity-60 pointer-events-none translate-x-1/3 -translate-y-1/4" data-id="element-119" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gold-primary/10 rounded-full blur-[120px] opacity-50 pointer-events-none -translate-x-1/4 translate-y-1/4" data-id="element-120" />
      <LightStreaks data-id="element-121" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10 w-full pt-20 pb-10 lg:py-0" data-id="element-122">
        {/* Mobile: Photo on top */}
        <div className="lg:hidden flex justify-center mb-6" data-id="element-123">
          <div className="relative w-48 h-48" data-id="element-124">
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, #1a3520 0%, transparent 70%)',
            filter: 'blur(8px)',
            transform: 'scale(1.3)'
          }} data-id="element-125" />
            <img src="https://cdn.magicpatterns.com/uploads/gsUsZKzX9Qcsejpe4oY27H/WhatsApp_Image_2026-04-06_at_11.42.01_AM.jpg" alt="Mariana Salazar" className="relative z-10 w-full h-full object-cover object-top rounded-full border-2 border-gold-primary/30" style={{
            filter: 'contrast(1.05) brightness(1.02)'
          }} data-id="element-126" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center" data-id="element-127">
          {/* Left Column: Text & CTA */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left max-w-2xl mx-auto lg:mx-0" data-id="element-128">
            {/* Personal Brand Identity */}
            <div className="mb-5 lg:mb-8" data-id="element-129">
              <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-warm-white font-semibold" data-id="element-130">
                Mariana Salazar
              </p>
              <p className="font-sans text-[0.65rem] sm:text-xs text-gold-light tracking-[0.2em] uppercase mt-1.5" data-id="element-131">
                {language === 'en' ? 'Licensed Insurance & Auto Sales Agent' : 'Agente de Seguros y Venta de Autos'}
              </p>
            </div>

            {/* Editorial Headline */}
            <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-warm-white leading-snug mb-6 lg:mb-8" data-id="element-132">
              {language === 'en' ? <>
                  Secure your legacy with{' '}
                  <span className="text-gradient-gold font-bold" data-id="element-133">premium</span>{' '}
                  financial guidance.
                </> : <>
                  Asegure su legado con asesoría financiera{' '}
                  <span className="text-gradient-gold font-bold" data-id="element-134">premium</span>.
                </>}
            </h3>

            {/* CTAs */}
            <a href="#contact" className="group inline-flex items-center justify-center gap-2.5 px-6 py-3 bg-brand-red text-white font-bold uppercase tracking-widest text-xs rounded hover:bg-red-700 hover:shadow-red-glow transition-all duration-300" data-id="element-135">
              {language === 'en' ? 'Request Consultation' : 'Solicitar Consulta'}
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" data-id="element-136" />
            </a>
          </div>

          {/* Right Column: Mariana Photo — Desktop only */}
          <div className="relative hidden lg:flex justify-center items-center h-[580px]" data-id="element-137">
            {/* Subtle glow behind photo */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, #1a3520 0%, #0D1F13 35%, #050A07 65%, transparent 100%)',
            filter: 'blur(12px)'
          }} data-id="element-138" />

            {/* Photo — seamless dark bg blend */}
            <div className="relative z-10 w-[460px] h-[560px] flex items-center justify-center" data-id="element-139">
              <img src="https://cdn.magicpatterns.com/uploads/gsUsZKzX9Qcsejpe4oY27H/WhatsApp_Image_2026-04-06_at_11.42.01_AM.jpg" alt="Mariana Salazar - MainPoint Multiservices" className="object-cover object-top w-full h-full" style={{
              maskImage: 'radial-gradient(ellipse 75% 70% at 50% 38%, black 45%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.2) 75%, transparent 90%)',
              WebkitMaskImage: 'radial-gradient(ellipse 75% 70% at 50% 38%, black 45%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.2) 75%, transparent 90%)',
              filter: 'drop-shadow(0 0 40px rgba(201,168,76,0.1)) contrast(1.05) brightness(1.02)'
            }} data-id="element-140" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}